/**
 * TenantSettingsCard — owner-only card for the Settings page.
 *
 * Surfaces the multi-tenant foundation built in Wave 30 (Chunks 10s/u/v):
 *   • Brand → primary/accent colour, logo URL, app name, AI name
 *   • Footer → "Powered by Gemini Space" toggle + free-text override
 *   • Modules → per-tenant feature toggles (recruitment/projects/team/…)
 *
 * Persists via:
 *   PUT /api/tenants/{tid}            (branding merge)
 *   PUT /api/tenants/{tid}/modules    (full module map)
 *
 * After save, calls TenantBrandingContext.refresh() so the SPA shell
 * (colours, footer, app name) updates without a page reload.
 */
import { useEffect, useState } from "react";
import {
  Building2, Loader2, Save, Palette, Settings as SettingsIcon, AlertCircle,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/TenantBrandingContext";

const MODULES = [
  { key: "recruitment",  label: "Recruitment",     hint: "Job postings, candidates, ranked CVs" },
  { key: "projects",     label: "Projects",        hint: "Project pipelines + profitability" },
  { key: "team",         label: "Team",            hint: "Attendance, leaderboard, leave" },
  { key: "sales_docs",   label: "Sales docs",      hint: "Quotes, sales orders, invoices, credit notes" },
  { key: "hidro",        label: "Hidro AI",        hint: "The unified Co-Pilot assistant" },
  { key: "research",     label: "Research",        hint: "Competitor watch, news & buying signals" },
  { key: "engineering",  label: "Engineering",     hint: "Job cards, site pins, vehicle ops" },
  { key: "gemini_shell", label: "Gemini Space shell", hint: "Show 'Powered by Gemini Space' footer + co-brand" },
];

export default function TenantSettingsCard() {
  const { user } = useAuth();
  const { refresh } = useBranding();
  const isOwner = (user?.role || "").toLowerCase() === "owner";
  const [tenantId, setTenantId] = useState("hidroverse");
  const [brand, setBrand] = useState(null);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");

  useEffect(() => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/tenants/me/branding");
        setTenantId(data.tenant_id);
        setBrand(data.branding || {});
        setModules(data.modules || {});
      } catch (e) {
        setErr(formatErr(e?.response?.data?.detail, "Couldn't load tenant settings."));
      } finally {
        setLoading(false);
      }
    })();
  }, [isOwner]);

  const setBrandField = (k, v) => setBrand((b) => ({ ...(b || {}), [k]: v }));
  const toggleModule = (k) => setModules((m) => ({ ...m, [k]: !m[k] }));

  const saveBrand = async () => {
    setSavingBrand(true);
    setErr("");
    setBanner("");
    try {
      await api.put(`/tenants/${tenantId}`, { branding: brand });
      await refresh();
      setBanner("Branding saved. Live across the app now.");
      setTimeout(() => setBanner(""), 4000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Branding save failed."));
    } finally {
      setSavingBrand(false);
    }
  };

  const saveModules = async () => {
    setSavingModules(true);
    setErr("");
    setBanner("");
    try {
      await api.put(`/tenants/${tenantId}/modules`, { modules });
      await refresh();
      setBanner("Modules saved.");
      setTimeout(() => setBanner(""), 4000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Modules save failed."));
    } finally {
      setSavingModules(false);
    }
  };

  if (!isOwner) return null;

  return (
    <div className="hv-card p-5" data-testid="tenant-settings-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-[#1FB6C1]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Tenant settings</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            Brand, AI persona, and per-tenant module toggles. Changes go live
            for every user in this workspace immediately after save.
          </p>

          {loading ? (
            <div className="mt-4 text-sm text-[#647087] flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : brand ? (
            <>
              {/* Brand section */}
              <div className="mt-5 border-t border-white/5 pt-4" data-testid="tenant-settings-brand-section">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#dde3ec] mb-3">
                  <Palette className="w-4 h-4 text-[#A78BFA]" /> Brand
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="App name">
                    <input
                      value={brand.app_name || ""}
                      onChange={(e) => setBrandField("app_name", e.target.value)}
                      className="hv-input text-sm w-full"
                      data-testid="tenant-brand-app-name"
                    />
                  </Field>
                  <Field label="AI name">
                    <input
                      value={brand.ai_name || ""}
                      onChange={(e) => setBrandField("ai_name", e.target.value)}
                      className="hv-input text-sm w-full"
                      placeholder="Hidro / Cerebrum / etc."
                      data-testid="tenant-brand-ai-name"
                    />
                  </Field>
                  <Field label="Primary colour">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={brand.primary_color || "#1FB6C1"}
                        onChange={(e) => setBrandField("primary_color", e.target.value)}
                        className="w-10 h-9 rounded-md border border-white/10 bg-transparent cursor-pointer"
                        data-testid="tenant-brand-primary-picker"
                      />
                      <input
                        value={brand.primary_color || ""}
                        onChange={(e) => setBrandField("primary_color", e.target.value)}
                        className="hv-input text-sm flex-1 font-mono"
                        placeholder="#1FB6C1"
                        data-testid="tenant-brand-primary-hex"
                      />
                    </div>
                  </Field>
                  <Field label="Accent colour">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={brand.accent_color || "#A78BFA"}
                        onChange={(e) => setBrandField("accent_color", e.target.value)}
                        className="w-10 h-9 rounded-md border border-white/10 bg-transparent cursor-pointer"
                        data-testid="tenant-brand-accent-picker"
                      />
                      <input
                        value={brand.accent_color || ""}
                        onChange={(e) => setBrandField("accent_color", e.target.value)}
                        className="hv-input text-sm flex-1 font-mono"
                        placeholder="#A78BFA"
                        data-testid="tenant-brand-accent-hex"
                      />
                    </div>
                  </Field>
                  <Field label="Logo URL (optional)" full>
                    <input
                      value={brand.logo_url || ""}
                      onChange={(e) => setBrandField("logo_url", e.target.value)}
                      className="hv-input text-sm w-full"
                      placeholder="https://…"
                      data-testid="tenant-brand-logo-url"
                    />
                  </Field>
                  <Field label="Footer text (optional)" full>
                    <input
                      value={brand.footer_text || ""}
                      onChange={(e) => setBrandField("footer_text", e.target.value)}
                      className="hv-input text-sm w-full"
                      placeholder="Free-text shown above the 'Powered by' line"
                      data-testid="tenant-brand-footer-text"
                    />
                  </Field>
                  <Field label="Support email">
                    <input
                      value={brand.support_email || ""}
                      onChange={(e) => setBrandField("support_email", e.target.value)}
                      className="hv-input text-sm w-full"
                      placeholder="support@yourcompany.com"
                      data-testid="tenant-brand-support-email"
                    />
                  </Field>
                  <Field label="Support phone">
                    <input
                      value={brand.support_phone || ""}
                      onChange={(e) => setBrandField("support_phone", e.target.value)}
                      className="hv-input text-sm w-full"
                      placeholder="+27 …"
                      data-testid="tenant-brand-support-phone"
                    />
                  </Field>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={saveBrand}
                    disabled={savingBrand}
                    className="hv-btn hv-btn-primary disabled:opacity-50"
                    data-testid="tenant-brand-save"
                  >
                    {savingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {savingBrand ? "Saving…" : "Save brand"}
                  </button>
                </div>
              </div>

              {/* Modules section */}
              <div className="mt-5 border-t border-white/5 pt-4" data-testid="tenant-settings-modules-section">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#dde3ec] mb-3">
                  <SettingsIcon className="w-4 h-4 text-[#FBBF24]" /> Modules
                </div>
                <p className="text-xs text-[#647087] mb-3">
                  Toggle modules off for this workspace. Disabled routes return
                  404 — the feature becomes invisible to users (cleanest UX
                  for SaaS plans).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MODULES.map((m) => {
                    const on = modules[m.key] !== false; // default true
                    return (
                      <label
                        key={m.key}
                        className="flex items-start gap-2 bg-white/[0.02] hover:bg-white/[0.04] rounded-md px-3 py-2 cursor-pointer border border-white/5"
                        data-testid={`tenant-module-row-${m.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleModule(m.key)}
                          className="mt-1 accent-[#1FB6C1]"
                          data-testid={`tenant-module-toggle-${m.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#dde3ec]">{m.label}</div>
                          <div className="text-[11px] text-[#647087] leading-tight">{m.hint}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={saveModules}
                    disabled={savingModules}
                    className="hv-btn hv-btn-primary disabled:opacity-50"
                    data-testid="tenant-modules-save"
                  >
                    {savingModules ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {savingModules ? "Saving…" : "Save modules"}
                  </button>
                </div>
              </div>

              {err && (
                <div className="mt-4 flex items-start gap-2 text-sm bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded-md px-3 py-2" data-testid="tenant-settings-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {err}
                </div>
              )}
              {banner && (
                <div className="mt-3 text-xs text-emerald-300" data-testid="tenant-settings-success">{banner}</div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-wider text-[#647087] mb-1">{label}</label>
      {children}
    </div>
  );
}
